import API_URL from '../config/apiUrl';
import type { EventPayload } from '../types';

class EventTracker {
  private queue: EventPayload[] = [];
  private sequence = 0;
  private flushTimer: number | null = null;
  private sessionId: string | null = null;
  private anonymousUserId: string | null = null;
  private experimentVersion = 'B';
  private platform: 'desktop' | 'mobile' = 'desktop';

  init(params: {
    sessionId: string;
    anonymousUserId: string;
    experimentVersion: string;
    platform: 'desktop' | 'mobile';
  }): void {
    this.sessionId = params.sessionId;
    this.anonymousUserId = params.anonymousUserId;
    this.experimentVersion = params.experimentVersion;
    this.platform = params.platform;
  }

  track(eventName: string, payload?: Record<string, unknown>): void {
    if (!this.sessionId || !this.anonymousUserId) {
      console.warn('[Analytics] Not initialized, dropping event:', eventName);
      return;
    }

    this.sequence += 1;
    const event: EventPayload = {
      eventId: `${this.sessionId}_${this.sequence.toString().padStart(3, '0')}`,
      anonymousUserId: this.anonymousUserId,
      sessionId: this.sessionId,
      experimentVersion: this.experimentVersion,
      platform: this.platform,
      eventName,
      timestamp: new Date().toISOString(),
      payload,
    };

    this.queue.push(event);
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = window.setTimeout(() => this.flush(), 2000);
  }

  async flush(): Promise<void> {
    if (this.flushTimer) {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    try {
      const response = await fetch(`${API_URL}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        keepalive: true,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (err) {
      // Silently drop failed analytics events in production to avoid console spam and requeue loops.
      // Backend is not always deployed for frontend-only tests.
      if (process?.env?.NODE_ENV === 'development' || (typeof location !== 'undefined' && location.hostname === 'localhost')) {
        console.warn('[Analytics] Failed to send events', err);
      }
    }
  }

  async flushSync(): Promise<void> {
    await this.flush();
  }
}

export const eventTracker = new EventTracker();
